import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Copyright,
  FileText,
  ShieldCheck,
} from 'lucide-react';
import {
  ERROR_MESSAGES,
  IMPORT_LIMITS,
  type ArticleImportResult,
  type TextbookCatalogNode,
  type UnitSummary,
} from '@en3/shared';
import { contentApi } from '@/api/content.api';
import { queryKeys } from '@/api/client';
import { isApiError, toDisplayMessage } from '@/api/errors';
import { useAuthStatus } from '@/api/hooks/useAuth';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/card';
import { FormSelect, FormTextarea } from './ArticleCard';

/**
 * 导入文章。
 *
 * 这个对话框承载了平台**唯一一条合规闸门**，因此有三处不能简化：
 *
 * 1. **授权确认必须由人勾选。** 复选框默认不勾，`acknowledgeCopyright` 只在勾选时传 true
 *    （见 `contentApi.importArticle` 的签名，它刻意不留默认值）。后端也会独立判一次，
 *    我们额外把 422 `COPYRIGHT_ACK_REQUIRED` 映射成同一句中文提示 ——
 *    这样即使用户绕过前端（脚本直接调接口）失败，前端复现同一路径时给出的解释也是一致的。
 *
 * 2. **正文长度上限与后端同一个常量**（`IMPORT_LIMITS.maxArticleChars`）。
 *    超限时**禁用提交并显示还差多少字**，而不是让用户提交后收 413 才知道。
 *    这里不禁用 textarea 本身：粘贴完超长的正文再让用户改掉几段，比"粘贴被吞掉"友好得多。
 *
 * 3. **成功后不关对话框，改为显示导入结果。**
 *    导入会立刻分句并给出难度分/CEFR，"到底导进来了什么"是这个页面最有价值的信息；
 *    直接关闭弹窗会让用户只剩一个不确定的 toast。
 */

export interface ImportArticleDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * 目录页已有的教材列表，直接复用以免对话框再请求一次目录。
   *
   * 类型刻意用 `TextbookCatalogNode[]`（而不是 `TextbookSummary[]`）：
   * 目录页拿到的就是带 `units` 的完整节点，单元下拉因此可以免请求联动。
   * 两者混在一个联合类型里会让"有没有 units"变成运行时判断，得不偿失。
   */
  textbooks: TextbookCatalogNode[];
  /** 新建单元后回到导入时预选 */
  defaultTextbookId?: string | undefined;
  /** 导入成功后由页面失效缓存（对话框本身不关，让用户看完结果） */
  onImported: () => void;
}

interface FormState {
  title: string;
  titleZh: string;
  author: string;
  source: string;
  sourceUrl: string;
  licenseNote: string;
  textbookId: string;
  unitId: string;
  visibility: 'public' | 'private';
  content: string;
  acknowledged: boolean;
}

const EMPTY_FORM: FormState = {
  title: '',
  titleZh: '',
  author: '',
  source: '',
  sourceUrl: '',
  licenseNote: '',
  textbookId: '',
  unitId: '',
  visibility: 'private',
  content: '',
  acknowledged: false,
};

/**
 * 导入对话框。
 *
 * **关闭时整个组件返回 null（= 卸载内部表单）**，这是刻意的：
 * 上一次导入的结果、以及「我确认拥有该文本的使用授权」的勾选，都绝不能带到下一次导入 ——
 * 否则一次勾选就变成长期有效的授权声明，这在合规上是不成立的。
 *
 * 用"卸载重建"而不是"打开时用 effect 重置 state"的原因有两条：
 * 1. 卸载重建天然没有残留，不需要维护一份"重置清单"（漏字段是迟早的事）；
 * 2. React 的 `set-state-in-effect` 规则明确不鼓励在 effect 里同步 setState ——
 *    那会多一轮无谓的渲染，而且在 StrictMode 下会被执行两次。
 */
export function ImportArticleDialog(props: ImportArticleDialogProps) {
  if (!props.open) return null;
  return <ImportArticleDialogInner {...props} />;
}

type InnerProps = Omit<ImportArticleDialogProps, 'open'>;

function ImportArticleDialogInner({
  onClose,
  textbooks,
  defaultTextbookId,
  onImported,
}: InnerProps) {
  const status = useAuthStatus();
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY_FORM,
    textbookId: defaultTextbookId ?? '',
  }));
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ArticleImportResult | null>(null);

  const authenticated = status === 'authenticated';

  const selectedTextbookId = form.textbookId;

  /**
   * 单元列表随教材联动。
   *
   * 目录页已经把单元带在响应里（`withUnits: true`），能直接用就不请求；
   * 只有当**选中的教材确实在列表里、却没带 units 字段**时才按需拉一次
   * `/api/textbooks/:id/units`。用"找不到这本教材"当作"需要请求"的信号会误判
   * （空单元列表是合法状态），所以这里用 `undefined` 而不是 `null` 表示"没数据"。
   */
  const selectedNode = textbooks.find((item) => item.id === selectedTextbookId);
  const embeddedUnits = selectedNode?.units;
  const needsUnitsRequest = embeddedUnits === undefined;

  const unitsQuery = useQuery({
    queryKey: queryKeys.textbook(selectedTextbookId),
    queryFn: () => contentApi.units(selectedTextbookId),
    // 只有真正打开时才去拉单元列表：关着的时候发请求纯属浪费（组件此刻已挂载，见外层包装）
    enabled: authenticated && selectedTextbookId !== '' && needsUnitsRequest,
  });

  const units: UnitSummary[] = embeddedUnits ?? unitsQuery.data?.items ?? [];
  const unitsLoading = needsUnitsRequest && unitsQuery.isPending && selectedTextbookId !== '';

  const charCount = form.content.length;
  const overLimit = charCount > IMPORT_LIMITS.maxArticleChars;
  const remaining = IMPORT_LIMITS.maxArticleChars - charCount;

  const canSubmit =
    authenticated &&
    !overLimit &&
    form.acknowledged &&
    form.title.trim().length > 0 &&
    form.content.trim().length > 0;

  function patch(next: Partial<FormState>) {
    setForm((current) => ({ ...current, ...next }));
  }

  const importMutation = useMutation({
    mutationFn: (input: Parameters<typeof contentApi.importArticle>[0]) => contentApi.importArticle(input),
  });

  async function submit() {
    setError(null);
    try {
      const data = await importMutation.mutateAsync({
        // 这里的 `true` 一定对应界面上被勾选的复选框（未勾选时按钮是禁用的）
        acknowledgeCopyright: true,
        title: form.title.trim(),
        content: form.content,
        visibility: form.visibility,
        ...(form.titleZh.trim() ? { titleZh: form.titleZh.trim() } : {}),
        ...(form.author.trim() ? { author: form.author.trim() } : {}),
        ...(form.source.trim() ? { source: form.source.trim() } : {}),
        ...(form.sourceUrl.trim() ? { sourceUrl: form.sourceUrl.trim() } : {}),
        ...(form.licenseNote.trim() ? { licenseNote: form.licenseNote.trim() } : {}),
        ...(form.textbookId ? { textbookId: form.textbookId } : {}),
        ...(form.unitId ? { unitId: form.unitId } : {}),
        importSource: 'paste',
      });
      setResult(data);
      onImported();
    } catch (cause) {
      // 422 与"没勾选"是同一件事的两种到达方式，因此给同一句提示，
      // 并把复选框退回未勾选状态：让用户重新确认一次，而不是直接重试。
      if (isApiError(cause) && cause.code === 'COPYRIGHT_ACK_REQUIRED') {
        setError(ERROR_MESSAGES.COPYRIGHT_ACK_REQUIRED);
        patch({ acknowledged: false });
        return;
      }
      setError(toDisplayMessage(cause));
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      variant="sheet"
      title={result ? '导入结果' : '导入文章'}
      className="sm:max-w-2xl"
      footer={
        result ? (
          <>
            <Button variant="outline" onClick={() => setResult(null)}>
              再导入一篇
            </Button>
            <Link
              to={ROUTES.reader(result.article.id)}
              className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm text-primary-foreground hover:opacity-90"
            >
              去阅读
            </Link>
          </>
        ) : authenticated ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              取消
            </Button>
            <Button onClick={() => void submit()} disabled={!canSubmit} loading={importMutation.isPending}>
              {overLimit ? '正文字数超限' : '开始导入'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>关闭</Button>
        )
      }
    >
      {result ? (
        <ImportResultSummary result={result} />
      ) : (
        <div className="space-y-4">
          {!authenticated && (
            <p className="rounded-md border border-dashed px-3 py-3 text-sm text-muted-foreground">
              导入需要先登录：正文与授权记录会绑定到你的账号。请先登录，再回到这里粘贴正文。
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="import-title" label="英文标题" hint="必填，用于列表与阅读页的主标题">
              <Input
                id="import-title"
                value={form.title}
                maxLength={IMPORT_LIMITS.maxTitleLength}
                onChange={(event) => patch({ title: event.target.value })}
                placeholder="例如：The way to success"
              />
            </Field>
            <Field id="import-title-zh" label="中文标题" hint="可选，填了会作为列表主标题">
              <Input
                id="import-title-zh"
                value={form.titleZh}
                maxLength={IMPORT_LIMITS.maxTitleLength}
                onChange={(event) => patch({ titleZh: event.target.value })}
                placeholder="例如：成功之路"
              />
            </Field>
            <Field id="import-author" label="作者" hint="可选">
              <Input
                id="import-author"
                value={form.author}
                onChange={(event) => patch({ author: event.target.value })}
              />
            </Field>
            <Field id="import-source" label="来源" hint="可选，例如：自撰 / 公开演讲稿 / 校本讲义">
              <Input
                id="import-source"
                value={form.source}
                onChange={(event) => patch({ source: event.target.value })}
              />
            </Field>
            <Field id="import-source-url" label="来源链接" hint="可选，便于日后核对授权">
              <Input
                id="import-source-url"
                value={form.sourceUrl}
                onChange={(event) => patch({ sourceUrl: event.target.value })}
                placeholder="https://…"
              />
            </Field>
            <Field id="import-license" label="授权说明" hint="可选，例如：外研社授权函编号 / 本人自撰">
              <Input
                id="import-license"
                value={form.licenseNote}
                onChange={(event) => patch({ licenseNote: event.target.value })}
              />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field id="import-textbook" label="所属教材" hint="不选则成为独立文章">
              <FormSelect
                id="import-textbook"
                value={form.textbookId}
                onChange={(event) => patch({ textbookId: event.target.value, unitId: '' })}
                className="w-full"
              >
                <option value="">不归入教材</option>
                {textbooks.map((textbook) => (
                  <option key={textbook.id} value={textbook.id}>
                    {textbook.title}
                  </option>
                ))}
              </FormSelect>
            </Field>

            <Field
              id="import-unit"
              label="所属单元"
              hint={unitsLoading ? '正在读取单元…' : '随教材联动'}
            >
              <FormSelect
                id="import-unit"
                value={form.unitId}
                disabled={selectedTextbookId === '' || unitsLoading}
                onChange={(event) => patch({ unitId: event.target.value })}
                className="w-full"
              >
                <option value="">暂不归入单元</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    Unit {unit.unitNo} · {unit.title}
                  </option>
                ))}
              </FormSelect>
            </Field>

            <Field
              id="import-visibility"
              label="可见性"
              hint={form.visibility === 'private' ? '仅自己可见，确认无误后再公开' : '所有人可见'}
            >
              <FormSelect
                id="import-visibility"
                value={form.visibility}
                onChange={(event) => patch({ visibility: event.target.value === 'public' ? 'public' : 'private' })}
                className="w-full"
              >
                <option value="private">私有（推荐）</option>
                <option value="public">公开</option>
              </FormSelect>
            </Field>
          </div>

          <Field id="import-content" label="正文" hint="纯英文本段文本，粘贴后会自动按段落与句子切分">
            <FormTextarea
              id="import-content"
              rows={10}
              value={form.content}
              onChange={(event) => patch({ content: event.target.value })}
              aria-invalid={overLimit}
              placeholder="把正文粘贴到这里…"
              className="font-serif"
            />
          </Field>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={overLimit ? 'text-destructive font-medium' : 'text-muted-foreground'}>
              正文 {charCount.toLocaleString('zh-CN')} 字符 / 上限{' '}
              {IMPORT_LIMITS.maxArticleChars.toLocaleString('zh-CN')}
            </span>
            {overLimit ? (
              <span className="text-destructive">超出 {Math.abs(remaining).toLocaleString('zh-CN')} 字符，请拆分后分批导入</span>
            ) : (
              <span className="text-muted-foreground">还可输入 {remaining.toLocaleString('zh-CN')} 字符</span>
            )}
            <Badge tone="muted">
              超过 {IMPORT_LIMITS.r2ThresholdChars.toLocaleString('zh-CN')} 字符会自动转存对象存储
            </Badge>
          </div>

          <label className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2.5 text-sm">
            <input
              type="checkbox"
              checked={form.acknowledged}
              onChange={(event) => patch({ acknowledged: event.target.checked })}
              className="mt-0.5 size-4 shrink-0 accent-[var(--primary)]"
            />
            <span>
              我确认拥有该文本的使用授权
              <span className="mt-0.5 block text-xs text-muted-foreground">
                平台不内置受版权保护的教材全文。导入正文会被记录（谁在什么时候导入了什么），
                请勿导入没有授权的作品。
              </span>
            </span>
          </label>

          {!form.acknowledged && (
            <p className="flex items-start gap-1.5 text-xs text-warning">
              <Copyright aria-hidden className="mt-0.5 size-3.5 shrink-0" />
              {ERROR_MESSAGES.COPYRIGHT_ACK_REQUIRED}
            </p>
          )}

          {authenticated && !canSubmit && (
            <p role="status" className="text-xs text-muted-foreground">
              还不能提交：
              {[
                form.title.trim() ? null : '标题未填',
                form.content.trim() ? null : '正文未粘贴',
                form.acknowledged ? null : '尚未勾选授权确认',
                overLimit ? '正文字数超限' : null,
              ]
                .filter((item): item is string => item !== null)
                .join('、')}
              。
            </p>
          )}

          {error && (
            <p role="alert" className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
              {error}
            </p>
          )}

          {overLimit && (
            <p className="text-xs text-destructive">
              正文字数已超过平台单篇上限，提交按钮已禁用。请把这篇拆成两到三篇分别导入。
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** 导入结果摘要：让用户立刻知道"切分成了什么、难不难"，而不是只有一个成功提示。 */
function ImportResultSummary({ result }: { result: ArticleImportResult }) {
  const { article, segmentation, difficulty, storage } = result;
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md border border-[color-mix(in_srgb,var(--success)_35%,transparent)] bg-[color-mix(in_srgb,var(--success)_10%,transparent)] px-3 py-3">
        <CheckCircle2 aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--success)]" />
        <div className="min-w-0">
          <p className="text-sm font-medium">导入成功：{article.titleZh ?? article.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            正文存放在 {storage === 'r2' ? '对象存储（长文）' : '数据库（短文）'}；
            {article.visibility === 'private' ? '当前是私有文章，只有你自己可见。' : '已公开，所有人都能读到。'}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="段落" value={segmentation.paragraphCount} />
        <Stat label="句子" value={segmentation.sentenceCount} />
        <Stat label="词数" value={segmentation.wordCount} />
        <Stat label="长难句" value={segmentation.longSentenceCount} tone={segmentation.longSentenceCount > 0 ? 'warning' : 'muted'} />
        <Stat label="平均句长" value={segmentation.avgSentenceLength.toFixed(1)} />
        <Stat label="难度分" value={Math.round(difficulty.score)} />
      </dl>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge tone="primary">CEFR 估计 {difficulty.cefr}</Badge>
        {difficulty.bandAvailable ? (
          <span>难度分基于分级词表与句法特征算出，可作为选择精读/泛读的依据。</span>
        ) : (
          <span>该文本的词表覆盖率偏低，难度分仅供参考。</span>
        )}
      </div>

      {!article.segmentStatus || article.segmentStatus !== 'done' ? (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <FileText aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          当前分句状态：{article.segmentStatus}。稍后刷新目录即可看到句子与长难句信息。
        </p>
      ) : null}

      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        已记录授权确认与版权状态（{article.copyrightStatus}），权利人来函时平台可据此下架。
      </p>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  tone?: 'default' | 'muted' | 'warning';
}) {
  return (
    <div className="rounded-md border px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-lg font-semibold">
        {value}
        {tone === 'warning' && <span className="ml-1 text-xs font-normal text-warning">需重点看</span>}
        {tone === 'muted' && <span className="ml-1 text-xs font-normal text-muted-foreground">这篇不难</span>}
      </dd>
    </div>
  );
}
